import React from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircleIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";

interface Version {
  id: number;
  major_version: number;
  minor_version: number;
  name: string;
  request_count: number;
  date_created: number;
}

interface VersionsTableProps {
  versions: Version[];
  loading: boolean;
  currentVersionId: string;
  emptyTitle?: string;
  emptyDescription?: string;
}

const VersionsTable: React.FC<VersionsTableProps> = ({
  versions,
  loading,
  currentVersionId,
  emptyTitle = "No versions found",
  emptyDescription = "Unable to load version history for this dataset."
}) => {
  const navigate = useNavigate();

  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const columns: PaginatedTableColumn<Version>[] = [
    {
      key: "major_version",
      header: "Version",
      width: "w-[20%]",
      render: (version) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-gc-med text-foreground">
            {version.major_version}.{version.minor_version}
          </span>
          {version.id.toString() === currentVersionId && (
            <CheckCircleIcon className="w-4 h-4 text-green-600" />
          )}
        </div>
      )
    },
    {
      key: "name",
      header: "Name",
      width: "w-[40%]",
      render: (version) => {
        const isCurrentVersion = version.id.toString() === currentVersionId;
        return (
          <div className="flex items-center gap-2">
            <span className={cn(
              "text-sm font-gc-reg",
              isCurrentVersion 
                ? "text-green-700 dark:text-green-400 font-gc-med" 
                : "text-foreground"
            )}>
              {version.name}
            </span>
            {isCurrentVersion && (
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                Current
              </span>
            )}
          </div>
        );
      }
    },
    {
      key: "request_count",
      header: "Requests",
      width: "w-[20%]",
      render: (version) => (
        <span className="text-sm font-gc-reg text-foreground">
          {version.request_count?.toLocaleString() || 0}
        </span>
      )
    },
    {
      key: "date_created",
      header: "Created",
      width: "w-[20%]",
      render: (version) => (
        <span className="text-sm font-gc-reg text-muted-foreground">
          {formatDate(version.date_created)}
        </span>
      )
    }
  ];

  const handleRowClick = (version: Version) => {
    navigate(`/build/datasets/${version.id}`);
  };

  const getRowClassName = (version: Version) => {
    const isCurrentVersion = version.id.toString() === currentVersionId;
    return cn(
      isCurrentVersion && "bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800"
    );
  };

  return (
    <PaginatedTable
      data={versions}
      columns={columns}
      loading={loading}
      currentPage={1}
      onPageChange={() => {}} // Not used since showPagination is false
      pageSize={versions.length}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      onRowClick={handleRowClick}
      rowClassName={getRowClassName}
      showPagination={false}
      loadingRows={5}
    />
  );
};

export default VersionsTable; 